import { useTranslation } from 'react-i18next';
import { Column, ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import ArrowDownIconSolid from '@/components/icons/ArrowDownIconSolid';
import { UserActivityItemResponseDto } from '../types/super-admin-dashboard-dto';
import { useSuperAdminDashboardFilter } from '../store/useSuperAdminDashboardFilter';
import EDateText from '@/components/everridge/EDateText';
import ActivityType from '../components/ActivityType';

const useUserActivityListColumns = (): ColumnDef<UserActivityItemResponseDto>[] => {
    const { t } = useTranslation();

    const { setFilter } = useSuperAdminDashboardFilter();

    const handleSort = (
        columnKey: Capitalize<keyof UserActivityItemResponseDto>,
        column: Column<UserActivityItemResponseDto>,
    ) => {
        const newDirection = column.getIsSorted() === 'asc';
        column.toggleSorting(newDirection);

        setFilter({
            orderBy: columnKey,
            orderDirection: newDirection ? 'asc' : 'desc',
        });
    };

    return [
        {
            accessorKey: 'date',
            header: ({ column }) => {
                return (
                    <Button
                        variant="link"
                        className={
                            'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
                        }
                        onClick={() => handleSort('Date', column)}
                    >
                        {t('main.superAdmin.dashboard.table.header.date')}
                        <div
                            className={`transition-transform duration-50 ease-in-out ${
                                column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                            }`}
                        >
                            {<ArrowDownIconSolid />}
                        </div>
                    </Button>
                );
            },
            cell: ({ row }) => (
                <div className={'text-sm text-popover-800'}>
                    <EDateText date={row.getValue('date')} />
                </div>
            ),
        },
        {
            accessorKey: 'userName',
            header: ({ column }) => {
                return (
                    <Button
                        variant="link"
                        className={
                            'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
                        }
                        onClick={() => handleSort('UserName', column)}
                    >
                        {t('main.superAdmin.dashboard.table.header.userName')}
                        <div
                            className={`transition-transform duration-50 ease-in-out ${
                                column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                            }`}
                        >
                            {<ArrowDownIconSolid />}
                        </div>
                    </Button>
                );
            },
            cell: ({ row }) => (
                <div className={'flex items-center gap-2.5'}>
                    <h4 className="text-sm text-popover-800 truncate">
                        {row.getValue('userName')}
                    </h4>
                </div>
            ),
        },
        {
            accessorKey: 'type',
            header: ({ column }) => {
                return (
                    <Button
                        variant="link"
                        className={
                            'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
                        }
                        onClick={() => handleSort('Type', column)}
                    >
                        {t('main.superAdmin.dashboard.table.header.type')}
                        <div
                            className={`transition-transform duration-50 ease-in-out ${
                                column.getIsSorted() === 'asc' ? '-rotate-180' : 'rotate-0'
                            }`}
                        >
                            {<ArrowDownIconSolid />}
                        </div>
                    </Button>
                );
            },
            cell: ({ row }) => (
                <div className={'flex items-center gap-2.5'}>
                    <h4 className="text-sm text-popover-800 truncate">
                        {<ActivityType type={row.getValue('type')} />}
                    </h4>
                </div>
            ),
        },
        {
            id: 'assignedTenants',
            header: () => (
                <span className="text-sm text-popover-1000 font-medium">
                    {t('main.superAdmin.dashboard.table.header.tenants')}
                </span>
            ),
            cell: ({ row }) => {
                const list = row.original.assignedTenants ?? [];
                const text = list
                    .map((x) => (x.name?.trim() ? x.name : x.tenantId))
                    .join(', ');
                return (
                    <div
                        className="text-sm text-popover-800 max-w-xs truncate"
                        title={text || undefined}>
                        {text || '—'}
                    </div>
                );
            },
        },
    ];
};

export default useUserActivityListColumns;
